import React, { useCallback, useRef, useState } from "react";

interface UploadDropZoneProps {
  onFileSelect: (file: File) => void;
  isLoading: boolean;
  hasParsed: boolean;
  fileName?: string;
}

const UploadDropZone: React.FC<UploadDropZoneProps> = ({
  onFileSelect,
  isLoading,
  hasParsed,
  fileName,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = "";
  };

  const borderColor = isDragging
    ? "border-blue-400"
    : hasParsed
    ? "border-green-300"
    : "border-gray-300";

  const bgColor = isDragging
    ? "bg-blue-50"
    : hasParsed
    ? "bg-green-50"
    : "bg-gray-50";

  return (
    <div
      className={`mt-4 rounded-xl border-2 border-dashed p-10 text-center transition-all duration-200 ${borderColor} ${bgColor} ${
        isLoading ? "cursor-default" : "cursor-pointer hover:border-blue-400 hover:bg-blue-50"
      }`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => !isLoading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleChange}
      />

      {isLoading ? (
        <div className="flex flex-col items-center gap-3">
          <svg
            className="h-12 w-12 animate-spin text-blue-500"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          <p className="text-sm text-gray-500">Parsing schedule…</p>
        </div>
      ) : hasParsed ? (
        <div className="flex flex-col items-center gap-2">
          <svg className="h-14 w-14 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-base font-bold text-green-700">File Parsed Successfully</p>
          <p className="text-sm text-gray-500">{fileName}</p>
          <p className="text-xs text-gray-400 mt-1">Click or drag to replace</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <svg className="h-14 w-14 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-base font-semibold text-gray-700">
            Drag &amp; drop your Excel file here
          </p>
          <p className="text-sm text-gray-400">or click to browse · Supports .xlsx and .xls</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              className="rounded-full border border-blue-400 px-5 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            >
              Choose File
            </button>
            <a
              href={`${process.env.PUBLIC_URL}/schedule_sample.xlsx`}
              download="schedule_sample.xlsx"
              className="flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3" />
              </svg>
              Download Sample
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDropZone;
